/**
 * **Disposable fixture — captured log lines.**
 *
 * Authored because there is no capture endpoint: nothing can read a customer's
 * port for us yet, so the "Recent Logs" panel needs something wire-shaped to
 * hand back. The lines are per Log Source *type* rather than per source, which
 * is also how the real thing behaves — what a capture returns is decided by the
 * device talking, not by the label we gave the listener.
 *
 * **Delete condition:** the first real capture endpoint. Deleted together with
 * `collectors.ts`.
 *
 * **Not demo-world content** (ADR 0017) — see `collectors.ts`.
 */
import { formatDate } from '@/shared/datetime/utils/format'

import type { LogSource } from '../types'

/**
 * Line templates per Log Source type. `{ts}` is replaced with the capture's own
 * clock reading, so a snapshot never shows a timestamp older than the button
 * press that produced it — the authored freshness trick `collectors.ts` uses for
 * last-seen, applied to text.
 */
const LINE_TEMPLATES: Record<string, string[]> = {
	'firewall': [
		'<134>{ts} asa-hq %ASA-6-302013: Built inbound TCP connection 105432 for outside:203.0.113.45/44321 to inside:10.44.1.20/443',
		'<134>{ts} asa-hq %ASA-6-302014: Teardown TCP connection 105388 for outside:198.51.100.9/51002 duration 0:00:31 bytes 4821',
		'<131>{ts} asa-hq %ASA-4-106023: Deny tcp src outside:203.0.113.77/60112 dst inside:10.44.1.20/3389 by access-group "outside_access_in"',
		'<134>{ts} asa-hq %ASA-6-305011: Built dynamic TCP translation from inside:10.44.1.51/52233 to outside:198.51.100.4/52233',
	],
	'ngfw': [
		'<14>{ts} pa-hq 1,2026/08/17 14:22:33,013201004541,TRAFFIC,end,2561,10.44.1.31,198.51.100.22,allow,web-browsing,tcp,443',
		'<14>{ts} pa-hq 1,2026/08/17 14:22:34,013201004541,THREAT,url,2561,10.44.1.31,203.0.113.10,block-url,malware,high',
		'<14>{ts} pa-hq 1,2026/08/17 14:22:35,013201004541,TRAFFIC,start,2561,10.44.1.44,198.51.100.71,allow,ssl,tcp,443',
	],
	'nxlog': [
		'<13>{ts} dc01 Microsoft-Windows-Security-Auditing: 4624 An account was successfully logged on. Account Name: svc_backup Logon Type: 3',
		'<13>{ts} dc01 Microsoft-Windows-Security-Auditing: 4625 An account failed to log on. Account Name: administrator Logon Type: 10',
		'<13>{ts} dc01 Microsoft-Windows-Security-Auditing: 4672 Special privileges assigned to new logon. Account Name: svc_backup',
	],
	'sd-wan': [
		'<134>{ts} mx-hq events type=vpn_connectivity_change vpn_type=site-to-site peer_contact=198.51.100.30 connectivity=true',
		'<134>{ts} mx-hq flows src=10.44.1.66 dst=198.51.100.14 protocol=udp sport=51820 dport=51820 pattern: allow all',
	],
}

/**
 * Lines every capture falls back to when the source's type has no authored
 * template — an unknown type is still a listening port, and an empty snapshot
 * would read as "nothing arrived" (a different, load-bearing state).
 */
const GENERIC_TEMPLATES = [
	'<134>{ts} 10.44.1.90 syslog: interface eth0 link state changed to up',
	'<134>{ts} 10.44.1.90 syslog: dhcp lease 10.44.1.148 assigned to 3c:22:fb:1a:04:9e',
]

/**
 * What a 30-second capture on this source would return.
 *
 * **Empty for a source that is not receiving.** That is the point of the panel:
 * a silent source with a listening port produces a capture with nothing in it,
 * and the UI has to be able to tell that from a capture that never ran.
 */
export function buildCaptureLines(source: LogSource, at: Date): string[] {
	if (source.status !== 'receiving') return []

	const templates = LINE_TEMPLATES[source.type] ?? GENERIC_TEMPLATES
	const timestamp = formatDate(at, 'MMM d HH:mm:ss')

	return templates.map((line) => line.replace('{ts}', timestamp))
}
